/**
 * The fairness page.
 *
 * Two jobs: explain the commit–reveal scheme in terms someone can actually
 * check, and give them the controls to check it. The verification runs on the
 * server by re-deriving the open from the revealed seed — but the same code
 * ships in `@exlyr/shared`, so anyone who does not trust our answer can run
 * it themselves against the inputs shown here.
 */
import { useState } from 'react'

import { EmptyState, RarityChip, SectionHeading, Skeleton } from '../components/ui'
import {
  useFairness,
  useHistory,
  useRotateSeeds,
  useSetClientSeed,
  useVerify,
} from '../hooks/queries'
import { useAuth } from '../hooks/useAuth'
import type { SeedPairView } from '../lib/api'
import { cents, dateTime, multiple, timeAgo } from '../lib/format'

export function FairnessPage() {
  const { user, connectAndSignIn } = useAuth()
  const { data, isLoading } = useFairness(Boolean(user))
  const { data: history } = useHistory(Boolean(user))
  const rotate = useRotateSeeds()
  const setClientSeed = useSetClientSeed()

  const [seedDraft, setSeedDraft] = useState('')
  const [verifying, setVerifying] = useState<string | null>(null)

  return (
    <div className="py-6">
      <SectionHeading
        title="Provably fair"
        sub="Every roll is fixed before you click, and checkable after."
      />

      <Explainer />

      {!user ? (
        <div className="mt-6">
          <EmptyState
            title="Sign in to see your seeds"
            body="Seed commitments are per-account, so yours needs a signed-in wallet."
            action={
              <button className="btn btn-primary btn-sm" onClick={() => void connectAndSignIn()}>
                Connect wallet
              </button>
            }
          />
        </div>
      ) : isLoading ? (
        <Skeleton className="mt-6 h-64" />
      ) : data ? (
        <>
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            <div className="panel p-5">
              <div className="flex items-baseline justify-between">
                <h3 className="text-base font-semibold">Your live commitment</h3>
                <span className="tnum text-xs" style={{ color: 'var(--color-faint)' }}>
                  {data.active.nonce} open{data.active.nonce === 1 ? '' : 's'} used
                </span>
              </div>

              <Field label="Server seed hash (published now)" value={data.active.serverSeedHash} />
              <Field label="Client seed (yours)" value={data.active.clientSeed} />
              <Field label="Next nonce" value={String(data.active.nonce)} />

              <div
                className="mt-4 rounded-lg border p-3 text-xs leading-relaxed"
                style={{ background: 'var(--color-surface-2)', color: 'var(--color-muted)' }}
              >
                The server seed behind that hash is not shown, and cannot be: that is what stops the
                next roll from being predictable. Rotate the pair to retire it, and the seed is
                revealed so every open made under it becomes checkable.
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <input
                  value={seedDraft}
                  onChange={(e) => setSeedDraft(e.target.value)}
                  placeholder="Set your own client seed"
                  maxLength={64}
                  className="panel-2 min-w-[180px] flex-1 px-3 py-2 font-mono text-xs outline-none"
                  style={{ color: 'var(--color-text)' }}
                />
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={seedDraft.trim().length < 4 || setClientSeed.isPending}
                  onClick={async () => {
                    await setClientSeed.mutateAsync(seedDraft.trim())
                    setSeedDraft('')
                  }}
                >
                  Use it
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={rotate.isPending}
                  onClick={() => void rotate.mutateAsync()}
                >
                  {rotate.isPending ? 'Rotating…' : 'Rotate & reveal'}
                </button>
              </div>

              {setClientSeed.isError && (
                <p className="mt-2 text-xs" style={{ color: 'var(--color-down)' }}>
                  {(setClientSeed.error as Error).message}
                </p>
              )}
              <p className="mt-2 text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                Changing the client seed retires the current pair too: reusing a nonce under two
                different client seeds would make the record ambiguous about which open it describes.
              </p>
            </div>

            <div className="panel p-5">
              <h3 className="text-base font-semibold">Revealed seeds</h3>
              <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
                Retired pairs, with the seed the hash was committed to. Hash each one yourself and
                check it matches what was published before you played.
              </p>

              <div className="mt-4 max-h-[22rem] space-y-3 overflow-y-auto pr-1">
                {data.retired.length === 0 ? (
                  <p className="py-6 text-center text-xs" style={{ color: 'var(--color-faint)' }}>
                    Nothing retired yet. Rotate above to reveal your first seed.
                  </p>
                ) : (
                  data.retired.map((pair) => <RetiredPair key={pair.id} pair={pair} />)
                )}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <SectionHeading
              title="Your opens"
              sub="Once a seed pair is retired, every open under it can be re-derived and checked."
            />

            {!history || history.length === 0 ? (
              <EmptyState title="No opens yet" body="Open a pack and it will show up here." />
            ) : (
              <div className="panel divide-y overflow-hidden">
                {history.map((open) => (
                  <div key={open.id}>
                    <div className="flex flex-wrap items-center gap-3 px-5 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-semibold">{open.packName}</span>
                          <RarityChip rarity={open.best} />
                        </div>
                        <div
                          className="tnum mt-0.5 text-[0.6875rem]"
                          style={{ color: 'var(--color-faint)' }}
                        >
                          nonce {open.nonce} · {timeAgo(open.openedAt)} · {dateTime(open.openedAt)}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="tnum text-sm font-semibold">{cents(open.totalCents)}</div>
                        <div
                          className="tnum text-[0.6875rem]"
                          style={{
                            color: open.multiple >= 1 ? 'var(--color-up)' : 'var(--color-faint)',
                          }}
                        >
                          {multiple(open.multiple)}
                        </div>
                      </div>

                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={!open.verifiable}
                        title={
                          open.verifiable
                            ? 'Re-derive this open from the revealed seed'
                            : 'Rotate your seed pair first: the seed for this open is still live'
                        }
                        onClick={() => setVerifying(verifying === open.id ? null : open.id)}
                      >
                        {verifying === open.id ? 'Hide' : open.verifiable ? 'Verify' : 'Seed live'}
                      </button>
                    </div>

                    {verifying === open.id && <VerifyPanel openId={open.id} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}

function Explainer() {
  const steps = [
    {
      n: 1,
      title: 'We commit',
      body: 'Before you open anything we generate a secret server seed and publish its SHA-256 hash. Publishing the hash locks us to that exact seed: we cannot change it later without the hash changing.',
    },
    {
      n: 2,
      title: 'You contribute',
      body: 'You supply the client seed. Because the outcome depends on both seeds, and we committed to ours before seeing yours, neither side can steer the result.',
    },
    {
      n: 3,
      title: 'The roll is derived',
      body: 'Every random number comes from HMAC-SHA256(server seed, client seed : nonce : round), consumed in a fixed order. Nothing else touches the outcome: no house adjustment, no re-roll.',
    },
    {
      n: 4,
      title: 'We reveal',
      body: 'Rotating your seed pair retires it and publishes the server seed. Hash it: it matches what we committed to. Re-derive the rolls: they match what you were paid.',
    },
  ]

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((step) => (
        <div key={step.n} className="panel p-4">
          <div
            className="grid size-6 place-items-center rounded-md text-xs font-bold"
            style={{ background: 'var(--color-brand-wash)', color: 'var(--color-brand)' }}
          >
            {step.n}
          </div>
          <h3 className="mt-3 text-sm font-semibold">{step.title}</h3>
          <p className="mt-1.5 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
            {step.body}
          </p>
        </div>
      ))}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-3">
      <div className="eyebrow">{label}</div>
      <div className="hash mt-1 select-all" style={{ color: 'var(--color-text)' }}>
        {value}
      </div>
    </div>
  )
}

function RetiredPair({ pair }: { pair: SeedPairView }) {
  return (
    <div className="panel-2 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="tnum text-xs font-semibold">
          {pair.nonce} open{pair.nonce === 1 ? '' : 's'}
        </span>
        <span className="text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
          retired {pair.retiredAt ? timeAgo(pair.retiredAt) : '-'}
        </span>
      </div>
      <div className="mt-2">
        <div className="eyebrow">Server seed (revealed)</div>
        <div className="hash select-all">{pair.serverSeed}</div>
      </div>
      <div className="mt-1.5">
        <div className="eyebrow">Committed hash</div>
        <div className="hash select-all">{pair.serverSeedHash}</div>
      </div>
      <div className="mt-1.5">
        <div className="eyebrow">Client seed</div>
        <div className="hash select-all">{pair.clientSeed}</div>
      </div>
    </div>
  )
}

function VerifyPanel({ openId }: { openId: string }) {
  const { data, isLoading, error } = useVerify(openId)

  if (isLoading) return <Skeleton className="mx-5 mb-4 h-28" />
  if (error) {
    return (
      <p className="px-5 pb-4 text-xs" style={{ color: 'var(--color-down)' }}>
        {(error as Error).message}
      </p>
    )
  }
  if (!data) return null

  const checks: Array<[string, boolean]> = [
    ['Commitment matches the published hash', data.checks.commitment],
    ['Rolls re-derive identically', data.checks.rolls],
    ['Rarities re-derive identically', data.checks.rarities],
    ['Assets re-derive identically', data.checks.symbols],
  ]

  return (
    <div
      className="border-t px-5 py-4"
      style={{ background: 'var(--color-surface-2)' }}
    >
      <div className="flex items-center gap-2">
        <span
          className="grid size-5 place-items-center rounded-none text-[0.625rem] font-bold"
          style={{
            background: data.verified ? 'var(--color-up)' : 'var(--color-down)',
            color: '#000',
          }}
          aria-hidden
        >
          {data.verified ? '✓' : '!'}
        </span>
        <span className="text-sm font-semibold">
          {data.verified ? 'Verified: this open re-derives exactly' : 'Verification failed'}
        </span>
      </div>

      <ul className="mt-3 space-y-1">
        {checks.map(([label, ok]) => (
          <li key={label} className="flex items-center gap-2 text-xs">
            <span style={{ color: ok ? 'var(--color-up)' : 'var(--color-down)' }}>
              {ok ? '✓' : '✕'}
            </span>
            <span style={{ color: 'var(--color-muted)' }}>{label}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <div className="eyebrow">Inputs</div>
          <div className="hash mt-1 space-y-0.5">
            <div className="select-all">seed {data.inputs.serverSeed}</div>
            <div className="select-all">client {data.inputs.clientSeed}</div>
            <div>nonce {data.inputs.nonce}</div>
            <div>pack {data.inputs.packId}</div>
          </div>
        </div>
        <div>
          <div className="eyebrow">Derived floats</div>
          <div className="hash mt-1">
            {data.floats.map((f) => f.toFixed(6)).join(', ')}
          </div>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ color: 'var(--color-faint)' }}>
              <th className="py-1 text-left font-semibold">Slot</th>
              <th className="py-1 text-left font-semibold">Booked</th>
              <th className="py-1 text-left font-semibold">Re-derived</th>
            </tr>
          </thead>
          <tbody>
            {data.booked.map((row, i) => {
              const replay = data.replayed[i]
              const same =
                replay && replay.symbol === row.symbol && replay.rarity === row.rarity
              return (
                <tr key={row.slotIndex} className="border-t">
                  <td className="py-1.5">{row.slotIndex + 1}</td>
                  <td className="py-1.5">
                    {row.symbol} · {row.rarity}
                  </td>
                  <td className="py-1.5" style={{ color: same ? 'var(--color-up)' : 'var(--color-down)' }}>
                    {replay ? `${replay.symbol} · ${replay.rarity}` : '-'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
